/**
 * Live sources: RTMP or SRT listeners an encoder or a phone app streams to.
 * Storage managers set them up; anyone who may upload starts and stops them.
 * While started, each stream that connects is recorded as received and shows
 * up in the library as a recording that can be watched live.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Loader2, Pencil, Play, Plus, Radio, Save, Square, Trash2 } from 'lucide-react';
import type { LiveSourceDTO, LiveTimecodeMode, SourceRights } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useCategories, useRightsSettings, useSchema } from '../../api/hooks';
import { RightsDefaultsBlock } from './RightsDefaults';
import { rightsDefaultsSummary } from './rightsDefaultsText';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { relative } from '../../lib/format';
import { copy } from '../../lib/clipboard';

interface List { items: LiveSourceDTO[]; canManage: boolean; canStart: boolean }

export const useLiveSources = () => useQuery({
  queryKey: ['live-sources'], queryFn: () => api<List>('/api/v1/live-sources'),
  refetchInterval: (q) => (q.state.data?.items.some((s) => s.armed || s.recording) ? 3000 : 15000),
});

/** Server URL and key the way phone apps such as Larix Broadcaster ask for them. */
function connection(s: LiveSourceDTO) {
  const u = s.url;
  if (s.protocol === 'rtmp') {
    const i = u.lastIndexOf('/');
    return { server: u.slice(0, i), key: u.slice(i + 1) };
  }
  return { server: u.split('?')[0]!, key: s.secret ?? '<passphrase>' };
}

/** 1.8.0: a live source's rights on its card, while rights management is on. */
function LiveRightsSummary({ source }: { source: LiveSourceDTO }) {
  const on = Boolean(useRightsSettings().data?.enabled);
  const schema = useSchema().data?.definition;
  const text = on ? rightsDefaultsSummary(schema, source.settings.rights?.defaults, source.settings.rights?.dates, 'recording') : null;
  return text ? <span className="faint" data-testid="live-rights-summary">© {text}</span> : null;
}

export function LiveSourcesAdmin() {
  const q = useLiveSources();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<LiveSourceDTO | 'new' | null>(null);
  const [guide, setGuide] = useState<LiveSourceDTO | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const canManage = q.data?.canManage ?? false;

  async function act(s: LiveSourceDTO, path: string, done: string, method: 'POST' | 'DELETE' = 'POST') {
    setBusy(s.id);
    try {
      await api(`/api/v1/live-sources/${s.id}${path}`, { method, ...(method === 'POST' ? { json: {} } : {}) });
      await qc.invalidateQueries({ queryKey: ['live-sources'] });
      toast(done);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page" data-testid="live-sources">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Live sources</h2>
        <span style={{ flex: 1 }} />
        {canManage && <button className="btn primary" onClick={() => setEditing('new')} data-testid="new-live-source"><Plus size={14} /> New live source</button>}
      </div>
      <p className="lede">Stream from an encoder or a phone app straight into PRISM over RTMP or SRT. While a source is started, every stream that connects is recorded as it arrives and appears in the library as a recording you can watch about 6 to 10 seconds behind live. When the stream ends it is processed like any upload. Start and stop sources here, or from a workflow on a schedule.</p>
      {q.isLoading ? <div className="skeleton" style={{ height: 120 }} /> : items.length === 0 ? (
        <EmptyState style={{ minHeight: 180 }} icon={<Radio size={24} />} title="No live sources yet">
          <p>{canManage ? 'Create one for each encoder or phone that streams to PRISM.' : 'Someone who manages storage can set one up.'}</p></EmptyState>
      ) : (
        <div className="cards">
          {items.map((s) => {
            const c = connection(s);
            return (
              <div className="panel-card" key={s.id} data-testid="live-source-card">
                <h3><Radio size={16} color={s.armed ? '#ff6b6f' : 'var(--accent-strong)'} /> {s.name}</h3>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="pill">{s.protocol.toUpperCase()} · port {s.port}</span>
                  {s.armed ? <span className="pill" style={{ color: '#ff6b6f' }} data-testid="live-source-state">{s.recording?.state === 'recording' ? 'Recording' : 'Listening'}</span>
                    : <span className="pill" data-testid="live-source-state">Stopped</span>}
                  {s.site !== 'local' && <span className="pill">Site {s.site}</span>}
                </div>
                <LiveRightsSummary source={s} />
                <dl className="kv">
                  <dt>Server</dt><dd className="mono">{c.server}</dd>
                  <dt>{s.protocol === 'rtmp' ? 'Stream key' : 'Passphrase'}</dt><dd className="mono">{s.secret ?? 'Shown to storage managers'}</dd>
                  {s.armed && <><dt>Started</dt><dd>{relative(s.armedAt)}</dd></>}
                  {s.recording?.state === 'recording' && s.recording.assetId && <><dt>Now</dt><dd><Link to={`/assets/${s.recording.assetId}`} data-testid="live-source-recording">Watch the recording</Link> · since {relative(s.recording.startedAt)}</dd></>}
                  {s.armed && s.recording?.state === 'waiting' && <><dt>Now</dt><dd className="muted">Waiting for a stream</dd></>}
                </dl>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {q.data?.canStart && (s.armed
                    ? <button className="btn sm" disabled={busy === s.id} onClick={() => void act(s, '/stop', `Stopped ${s.name}`)} data-testid="live-source-stop"><Square size={12} /> Stop</button>
                    : <button className="btn sm primary" disabled={busy === s.id} onClick={() => void act(s, '/start', `${s.name} is listening`)} data-testid="live-source-start"><Play size={12} /> Start</button>)}
                  {s.secret && <button className="btn ghost sm" onClick={() => setGuide(s)}><Copy size={13} /> Phone app setup</button>}
                  {canManage && <button className="btn ghost sm" onClick={() => setEditing(s)}><Pencil size={13} /> Edit</button>}
                  {canManage && !s.armed && <button className="btn ghost sm" onClick={() => window.confirm(`Make a new ${s.protocol === 'rtmp' ? 'stream key' : 'passphrase'} for “${s.name}”? Encoders using the old one can no longer stream.`) && void act(s, '/secret', 'New key made')}><KeyRound size={13} /> New key</button>}
                  {canManage && !s.armed && <button className="btn ghost sm danger" onClick={() => window.confirm(`Remove “${s.name}”? Recordings already made stay in the library.`) && void act(s, '', `Removed ${s.name}`, 'DELETE')}><Trash2 size={13} /> Remove</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && <SourceDialog source={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {guide && <Guide source={guide} onClose={() => setGuide(null)} />}
    </div>
  );
}

function Guide({ source: s, onClose }: { source: LiveSourceDTO; onClose: () => void }) {
  const c = connection(s);
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="field"><label>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}><code className="code-block" style={{ flex: 1 }}>{value}</code><button className="btn ghost sm" onClick={() => copy(value)} aria-label={`Copy ${label}`}><Copy size={13} /></button></div>
    </div>
  );
  return (
    <Modal title={`Stream to ${s.name}`} onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
      <div className="form" style={{ gap: 12 }}>
        <p className="muted" style={{ margin: 0 }}>In a phone app such as Larix Broadcaster (iOS and Android), add a connection with these settings, then press record in the app while the source is started here.</p>
        {s.protocol === 'rtmp' ? (
          <>
            <Row label="URL (with the key)" value={s.url} />
            <Row label="Server only" value={c.server} />
            <Row label="Stream key" value={c.key} />
            <p className="faint" style={{ margin: 0 }}>Apps that ask for one URL take the first; apps that ask for a server and a key take the other two.</p>
          </>
        ) : (
          <>
            <Row label="URL" value={c.server} />
            <Row label="Passphrase" value={c.key} />
            <p className="faint" style={{ margin: 0 }}>Mode: Caller (PRISM listens). Encryption on, with the passphrase above copied exactly; any key length (AES-128, 192 or 256). Stream ID: leave empty. Latency: 200 ms or more on mobile networks. A passphrase made with New key applies from the next start.</p>
          </>
        )}
        <p className="faint" style={{ margin: 0 }}>PRISM records what the app sends, as it sends it: choose resolution, frame rate and bitrate in the app. The phone must reach this address and port; on a firewall, open {s.protocol === 'rtmp' ? 'TCP' : 'UDP'} port {s.port}.</p>
      </div>
    </Modal>
  );
}

function SourceDialog({ source, onClose }: { source: LiveSourceDTO | null; onClose: () => void }) {
  const qc = useQueryClient();
  const categories = useCategories().data?.items ?? [];
  const [name, setName] = useState(source?.name ?? '');
  const [protocol, setProtocol] = useState<'rtmp' | 'srt'>(source?.protocol ?? 'rtmp');
  const [port, setPort] = useState(String(source?.port ?? 1935));
  const [titlePrefix, setTitlePrefix] = useState(source?.settings.titlePrefix ?? '');
  const [categoryId, setCategoryId] = useState(source?.settings.categoryId ?? '');
  // 1.9.1: new sources start on time of day; a source saved before 1.9.1 has none and stays on the stream's timecode.
  const [timecode, setTimecode] = useState<LiveTimecodeMode>(source ? source.settings.timecode ?? 'stream' : 'time_of_day');
  // 1.8.0: the rights every recording gets (settings.rights), with dates relative to the recording's start.
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  const schema = useSchema().data?.definition;
  const [rights, setRights] = useState<SourceRights>(source?.settings.rights ?? { defaults: {}, dates: {} });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const hasRights = Object.keys(rights.defaults).length > 0 || Object.keys(rights.dates).length > 0;
      // The rights stay as they were while rights management is off (they are not shown then).
      const keep = rightsOn ? (hasRights || source?.settings.rights ? { rights } : {}) : source?.settings.rights ? { rights: source.settings.rights } : {};
      // PATCH replaces the settings whole, so the timecode is always sent.
      const settings = { titlePrefix: titlePrefix.trim() || undefined, categoryId: categoryId || null, waitMinutes: source?.settings.waitMinutes ?? 720, timecode, ...keep };
      if (source) await api(`/api/v1/live-sources/${source.id}`, { method: 'PATCH', json: { name, port: Number(port), settings } });
      else await api('/api/v1/live-sources', { method: 'POST', json: { name, protocol, port: Number(port), settings } });
      await qc.invalidateQueries({ queryKey: ['live-sources'] });
      toast(source ? `Saved ${name}` : `Created ${name}`);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={source ? `Edit ${source.name}` : 'New live source'} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={saving || !name.trim() || !(Number(port) >= 1024)} onClick={() => void save()} data-testid="save-live-source">
        {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {source ? 'Save' : 'Create'}
      </button>
    </>}>
      <div className="form" style={{ gap: 14 }}>
        <div className="field"><label htmlFor="ls-name">Name</label><input id="ls-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Reporter phone" /></div>
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label>Protocol</label>
            <div className="segmented">
              <button aria-pressed={protocol === 'rtmp'} disabled={Boolean(source)} onClick={() => { setProtocol('rtmp'); if (port === '9000') setPort('1935'); }}>RTMP</button>
              <button aria-pressed={protocol === 'srt'} disabled={Boolean(source)} onClick={() => { setProtocol('srt'); if (port === '1935') setPort('9000'); }}>SRT</button>
            </div>
          </div>
          <div className="field"><label htmlFor="ls-port">Port</label><input id="ls-port" className="input" type="number" min={1024} max={65535} value={port} onChange={(e) => setPort(e.target.value)} /></div>
        </div>
        <p className="faint" style={{ margin: 0 }}>{protocol === 'rtmp' ? 'RTMP works with almost every phone app and encoder. PRISM makes a stream key.' : 'SRT copes better with mobile networks and is encrypted. PRISM makes a passphrase.'} Each source needs its own port, open on the firewall to the live worker.</p>
        <div className="field"><label htmlFor="ls-title">Recording titles</label><input id="ls-title" className="input" value={titlePrefix} onChange={(e) => setTitlePrefix(e.target.value)} maxLength={120} placeholder={name || 'The source name'} />
          <span className="faint">Followed by the date and time the stream started, in the workspace’s time zone (Administration → Rights).</span></div>
        <div className="field"><label htmlFor="ls-timecode">Timecode</label>
          <select id="ls-timecode" className="input" value={timecode} onChange={(e) => setTimecode(e.target.value as LiveTimecodeMode)} data-testid="live-timecode">
            <option value="time_of_day">Time of day (workspace time zone)</option>
            <option value="stream">From the stream</option>
          </select>
          <span className="faint" data-testid="live-timecode-help">{timecode === 'time_of_day'
            ? 'A recording starts at the clock time its stream reached PRISM (to about a second), counting on through midnight and summer-time changes. Changes apply from the next recording.'
            : 'The timecode the encoder sends with the stream, or 00:00:00:00 when it sends none.'}</span></div>
        <div className="field"><label htmlFor="ls-cat">Asset class</label>
          <select id="ls-cat" className="input" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">None</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {rightsOn && schema && (
          <RightsDefaultsBlock schema={schema} defaults={rights.defaults} onDefaults={(d) => setRights((r) => ({ ...r, defaults: d }))}
            dates={rights.dates} onDates={(d) => setRights((r) => ({ ...r, dates: d ?? {} }))} relativeTo="recording" testId="live-rights-block" />
        )}

      </div>
    </Modal>
  );
}
